import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap";

/**
 * Close-Job Supervisor Override modal.
 *
 * Legacy parity: frmSupvOv.vb (close job when pieces remaining or
 * nonconformities exist). Returns Number(empId) via onConfirm so the
 * parent passes it to CloseJobQuality as QualityClosedBy.
 *
 * Password verification: intentionally NOT included (product decision).
 *   Go-live parity (2026-09-24): legacy frmSupvOv made the supervisor
 *   authenticate at the inspector's PC; on the web the supervisor's own
 *   SIGN-IN is that authentication. The backend
 *   (InspectionController.CloseJobQuality) authorizes the non-compliant
 *   close only when the SIGNED-IN caller holds "DI - Supervisor Override"
 *   and records that caller (QualityCloseBy / NoncomformityOverrideBy) —
 *   a typed-in EmpID no longer authorizes anything, so the field below
 *   shows the signed-in EmpID read-only.
 *
 * UI-consistency rewrite using reactstrap.
 */
const FrmSupvOv = ({
  open,
  signedInEmpId,
  pcsRemaining,
  nonConformities,
  onCancel,
  onConfirm,
}) => {
  const [empId, setEmpId] = useState("");

  useEffect(() => {
    // Go-live parity (2026-09-24): prefilled with the signed-in EmpID.
    setEmpId(open && Number(signedInEmpId) > 0 ? String(signedInEmpId) : "");
  }, [open, signedInEmpId]);

  const valid = useMemo(() => {
    const n = Number(empId);
    return Number.isInteger(n) && n > 0;
  }, [empId]);

  const safePcsRemaining = useMemo(
    () => Number(pcsRemaining || 0),
    [pcsRemaining]
  );
  const safeNonConformities = useMemo(
    () => Number(nonConformities || 0),
    [nonConformities]
  );

  const handleEmpIdChange = useCallback((e) => {
    setEmpId(e.target.value);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm?.(Number(empId));
  }, [onConfirm, empId]);

  return (
    <Modal isOpen={!!open} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        <i className="ri-shield-keyhole-line align-bottom me-2 text-warning"></i>
        Supervisor Override
        <Badge color="light" className="ms-2 text-muted">
          Close Job
        </Badge>
      </ModalHeader>
      <ModalBody>
        <div className="text-muted small mb-3">
          <div>
            <strong>Pieces Not Inspected:</strong> {safePcsRemaining}
          </div>
          <div>
            <strong>Non Conformities:</strong> {safeNonConformities}
          </div>
        </div>

        <Form onSubmit={(e) => e.preventDefault()}>
          <FormGroup className="mb-2">
            <Label for="supv-empid">Closed By (signed-in supervisor)</Label>
            <Input
              id="supv-empid"
              type="number"
              inputMode="numeric"
              value={empId}
              onChange={handleEmpIdChange}
              readOnly
            />
          </FormGroup>
          <div className="form-text text-muted">
            The override is recorded under the signed-in user, who must hold
            the &quot;DI - Supervisor Override&quot; permission. If you do not
            have it, ask a supervisor to sign in and close the job.
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" disabled={!valid} onClick={handleConfirm}>
          <i className="ri-stop-circle-line align-bottom me-1"></i>
          Override & Close Job
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(FrmSupvOv);
